import { LanguageCode, OtpType, languageCodeStringToEnum, FlowType, flowTypeEnumToString } from "@/enums";
import { loading, replaceState, getTranslations, translation, getRivertyDomain, isValidMerchantUrl } from "@/utils/common";
import { CheckoutInfo, OtpResponse, TextResourceList } from "@/entities";
import { getTextResources } from "../../../tests/entities";
import { get } from "@/utils/httpRequest";
import { defineComponent } from "vue";
import { otpStore } from "@/store";
import { OtpCode } from "@/entities/checkoutInfo";

const maxOtpPerType = 4;

export default defineComponent({
    name: "SelectOtpTypeView",
    props: {
        scaSessionId: {
            type: String,
            required: true,
        },
    },
    data: function () {
        return {
            data: new CheckoutInfo(),
            redirectUrl: <string | null>"",
            selectedOtpType: OtpType.Unset,
            textResourceList: new TextResourceList(),
            OtpType,
            requestCodeDisabled: true,
            emailDisabled: false,
            smsDisabled: false,
            store: otpStore(),
        };
    },
    async created() {
        replaceState();
        this.data = this.store.otp;
        this.redirectUrl = this.data.redirectUrl;

        loading();
        try {
            const response = <OtpResponse>await get(`api/verifyotp/init/${this.scaSessionId}`);

            //redirect to error (or restart) view
            if (!response.isSuccess) {
                this.$router.replace(`/${response.view}/${this.scaSessionId}`);
            }

            //redirect back to merchant
            if (response.data && response.data.redirectUrl && response.returnToMerchant) {
                window.location.replace(response.data.redirectUrl);
            }

            this.data = response.data ?? new CheckoutInfo();
            this.store.otp = this.data;
            this.setDisabledState(this.data.otpInfo);
        } catch (e) {
            console.log("Error while loading page data.", e);
        } finally {
            loading();
        }

        this.setDisabledState(this.data.otpInfo);
    },
    mounted() {
        this.getData();
    },
    methods: {
        async getData() {
            loading();

            try {
                this.textResourceList = (await this.getTextResources(this.data.countryCode, languageCodeStringToEnum(this.data.conversationLanguage))) as TextResourceList;
            } catch {
                return;
            } finally {
                loading();
            }
        },
        setDisabledState(otpInfo: OtpCode[]) {
            const emailCount = otpInfo.filter((item) => item.otpType == OtpType[OtpType.Email]).length;
            const smsCount = otpInfo.filter((item) => item.otpType == OtpType[OtpType.Sms]).length;
            this.emailDisabled = emailCount >= maxOtpPerType;
            this.smsDisabled = smsCount >= maxOtpPerType;
            
            if (this.emailDisabled) {
                this.changeOtpType(OtpType.Sms);
            } else if (this.smsDisabled) {
                this.changeOtpType(OtpType.Email);
            }
        },
        hasSteps() {
            return this.textResourceList.textResourceItems.length > 0 && (this.data.flowType == flowTypeEnumToString(FlowType.VerifyOtpEmailDob) || this.data.flowType == flowTypeEnumToString(FlowType.VerifyOtpSmsDob));
        },
        changeOtpType(newValue: OtpType) {
            if (this.emailDisabled && newValue == OtpType.Email) return;
            if (this.smsDisabled && newValue == OtpType.Sms) return;

            this.selectedOtpType = newValue;
            const btn = document.getElementById("redirectBtn");

            if (this.selectedOtpType != OtpType.Unset && btn) {
                btn.removeAttribute("disabled");
            }
        },
        async goBack() {
            //change status to "UserCancel" and redirect to merchant
            const response = await get(`api/verifyotp/cancel/${this.scaSessionId}`);
            if (!response.isSuccess) {
                //redirect to error view
                this.$router.replace(`/${response.view}/${this.scaSessionId}`);
            }

            if (response.data.redirectUrl && isValidMerchantUrl(response.data.redirectUrl)) {
                window.location.replace(response.data.redirectUrl);
            } else {
                // Redirect to error page if merchant URL is invalid
                this.$router.replace(`/error/${this.scaSessionId}?merchantUrl=${response.data.redirectUrl || 'invalid'}`);
            }
        },
        async redirect() {
            if (this.selectedOtpType == OtpType.Unset) return;

            const otpType = this.selectedOtpType == OtpType.Email ? "Email" : "Sms";
            const response = <OtpResponse>await get(`api/verifyotp/requestcode/${otpType}/${this.scaSessionId}`);

            if (response.data) {
                this.data = response.data;
                this.store.otp = response.data;
            }

            //redirect to error view
            if (!response.isSuccess) {
                this.$router.replace(`/${response.view}/${this.scaSessionId}`);
            }

            //redirect back to merchant
            if (response.data && response.data.redirectUrl && response.returnToMerchant) {
                if (isValidMerchantUrl(response.data.redirectUrl)) {
                    window.location.replace(response.data.redirectUrl);
                } else {
                    // Redirect to error page if merchant URL is invalid
                    this.$router.replace(`/error/${this.scaSessionId}?merchantUrl=${response.data.redirectUrl}`);
                }
            }

            //redirect to next step
            if (response.view) {
                this.$router.replace(`/${response.view}/${this.scaSessionId}`);
            }
        },
        async getTextResources(countryCode: string | null | undefined, languageCode: LanguageCode, agent: unknown | null = null): Promise<TextResourceList | getTextResources> {
            const sysnameList = [
                "OtpClose",
                "OtpIntroTitle",
                "OtpIntroDescription",
                "OtpIntroSmsAndEmailDescription",
                "OtpSelectMethodViaEmail",
                "OtpSelectMethodViaSms",
                "OtpRequestCode",
                "OtpStart",
                "OtpEmailCode",
                "OtpSmsCode",
                "OtpBirthDateLabel",
            ];

            return await getTranslations(sysnameList, countryCode, languageCode, agent);
        },
        getTranslation(sysName: string, replace: string[] = []): string {
            return translation(this.textResourceList?.textResourceItems, sysName, replace);
        },
    },
});
